import React, { useEffect } from "react";
import MapProjectBoundingBoxLayer from "./MapProjectBoundingBoxLayer";
import bbox from "@turf/bbox";
import { Feature, MultiPolygon, Point, Polygon } from "geojson";
import { Layer } from "../../types/layer";
import { Project } from "../../types/project";


export interface LayerState {
  active: boolean;
  stale: boolean;
  name: string;
  geojson: Feature<Polygon | MultiPolygon> | null;
}

interface ProjectLayersManagerProps {
  projects: Project[];
  onLayersUpdate: (layers: Layer[]) => void;
  layerStates: Record<string, LayerState>;
  setLayerStates: React.Dispatch<
    React.SetStateAction<Record<string, LayerState>>
  >;
  onProjectClick?: (projectId: string) => void | Promise<void>;
}

const ProjectLayersManager: React.FC<ProjectLayersManagerProps> = ({
  projects,
  onLayersUpdate,
  layerStates,
  setLayerStates,
  onProjectClick,
}) => {
  // Track all layer states in a single component

  // Process each project - initialize state and detect changes
  useEffect(() => {
    const updatedStates = { ...layerStates };
    Object.keys(updatedStates).map((state) => {
      updatedStates[state].active = false;
    });
    let hasChanges = false;

    projects.forEach((project) => {
      const id = `project-${project.id}`;
      const isProcessing = project.status === 'processing' && !!project.task_id;
      const isExpanding = project.status === 'expanding' && !!project.task_id;
      const isRefreshing = project.status === 'refreshing';
      const displayName = `${project.name}${isProcessing ? ' (Initializing…)' : isExpanding ? ' (Loading tile…)' : isRefreshing ? ' (Refreshing data…)' : ''}`;

      // Initialize state if it doesn't exist
      if (!updatedStates[id]) {
        updatedStates[id] = {
          active: true,
          stale: true,
          name: displayName,
          geojson: generateGeoJSON(project, id),
        };
        hasChanges = true;
      } else {
        updatedStates[id] = {
          ...updatedStates[id],
          name: displayName,
          stale: true,
          active: true,
        };
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setLayerStates(updatedStates);
    }
  }, [projects]);

  // Helper function to generate GeoJSON from project boundary
  const generateGeoJSON = (project: Project, id: string): Feature<Polygon | MultiPolygon> | null => {
    if (!project.boundary) return null;
    return {
      type: "Feature",
      properties: { id },
      geometry: project.boundary as Polygon | MultiPolygon,
    };
  };

  // Create and send layer objects to parent component
  useEffect(() => {
    // Skip if no layer states yet
    if (Object.keys(layerStates).length === 0) {
      return;
    }

    // Process all projects and create layer objects
    const layers = Object.keys(layerStates)
      .flatMap((id) => {
        const state = layerStates[id];

        // Skip if state doesn't exist
        if (!state) {
          return [];
        }

        // Toggle layer state (for toolbar)
        const toggleActive = () => {
          setLayerStates((prev) => ({
            ...prev,
            [id]: {
              ...prev[id],
              active: !prev[id].active,
            },
          }));
        };

        // Set stale to trigger regeneration
        const setStale: React.Dispatch<React.SetStateAction<boolean>> = (
          value
        ) => {
          setLayerStates((prev) => ({
            ...prev,
            [id]: {
              ...prev[id],
              stale:
                typeof value === "function" ? value(prev[id].stale) : value,
            },
          }));
        };

        // Create base layer (bbox fill + stroke). Do NOT render text inside polygon to avoid repetition.
        // Infer processing state from name suffix
        const isInitializing = state.name.includes('Initializing…');
        const isExpandingTile = state.name.includes('Loading tile…');
        const projectForLayer = projects.find(p => `project-${p.id}` === id);
        const hasOpenIncidents = (projectForLayer?.open_incident_count ?? 0) > 0;
        // Everything the two layers' styles are built from. Map rebuilds a
        // layer when this changes; its update path only replaces the data,
        // which left a project grey and labelled "Initializing…" on the map
        // after it had finished.
        const styleKey = `${isInitializing ? "init" : isExpandingTile ? "expand" : "ready"}|${
          projectForLayer?.open_incident_count ?? 0
        }`;
        const layer = MapProjectBoundingBoxLayer(
          id,
          state.name,
          false,
          true,
          state.active,
          state.stale,
          setStale,
          state.geojson,
          toggleActive,
          isInitializing,
          hasOpenIncidents
        );
        layer.styleKey = styleKey;
        // Attach click handler for navigation if provided; only block during initial creation
        if (onProjectClick) {
          const projectId = id.replace(/^project-/, "");
          layer.onClick = () => {
            if (!isInitializing) {
              onProjectClick(projectId);
            }
          };
        }
        // Build a label layer placed just outside the top-left corner
        const nameText = projectForLayer?.name ?? state.name.replace(/\s*\((Initializing|Loading tile)…\)$/, '');
        const openIncidents = projectForLayer?.open_incident_count ?? 0;
        // Show status label when applicable
        const labelContent = isInitializing
          ? ['format', nameText, {}, ' (', {}, 'Initializing…', { 'font-scale': 0.95, 'text-color': '#94a3b8' }, ')']
          : isExpandingTile
          ? ['format', nameText, {}, ' (', {}, 'Loading tile…', { 'font-scale': 0.95, 'text-color': '#94a3b8' }, ')']
          : hasOpenIncidents
          ? ['format', nameText, {}, ` · ${openIncidents} open incident${openIncidents === 1 ? '' : 's'}`, { 'font-scale': 0.95, 'text-color': '#f59e0b' }]
          : nameText;

        // No geometry (init failed before a boundary was written): the base
        // layer is dropped downstream for lack of a source, and a label with
        // nowhere to go would otherwise land at 0,0.
        if (!state.geojson) {
          return [layer];
        }
        // Label offset from the extent. @turf/bbox handles MultiPolygon
        // boundaries (expanded / L-shaped projects); reading coordinates[0]
        // as a ring gave NaN for those.
        const [minLng, minLat, maxLng, maxLat] = bbox(state.geojson);
        const width = Math.abs(maxLng - minLng);
        const height = Math.abs(maxLat - minLat);
        const dx = Math.max(width * 0.05, 0.003);
        const dy = Math.max(height * 0.05, 0.003);
        const labelLng = minLng - dx;
        const labelLat = maxLat + dy;

        const labelFeature: Feature<Point> = {
          type: 'Feature',
          properties: { id: `${id}-kpi` },
          geometry: {
            type: 'Point',
            coordinates: [labelLng, labelLat],
          },
        };

        // Name label (always visible) with modern styling
        const nameSymbolStyle = {
          id: `${id}-name-text`,
          type: 'symbol',
          layout: {
            'text-field': labelContent,
            'text-size': ['interpolate', ['linear'], ['zoom'], 4, 14, 8, 16, 12, 18, 16, 20],
            'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
            'text-line-height': 1.1,
            'text-justify': 'left',
            'text-anchor': 'top-left',
            'text-allow-overlap': true,
            'text-ignore-placement': true,
            'text-padding': 2,
            'text-letter-spacing': 0.02,
            'text-transform': 'none',
          },
          paint: {
            'text-color': '#f8fafc', // Slate-50
            'text-halo-color': 'rgba(15, 23, 42, 0.95)', // Slate-900 with high opacity
            'text-halo-width': 2.5,
            'text-halo-blur': 1,
          },
        } as const;

        // We no longer need a separate metrics label style

        const labelLayer: Layer = {
          id: `${id}-name`,
          name: `${state.name} Label`,
          source: labelFeature,
          layerStyle: [nameSymbolStyle],
          onTop: true,
          toolbarButton: undefined,
          active: state.active,
          stale: state.stale,
          setStale,
          type: 'project',
          interactiveLayerIds: [],
          styleKey,
        };

        return [layer, labelLayer];
      })
      .filter((layer): layer is Layer => !!layer);

    // Only filter by active state and valid source
    const activeLayers = layers.filter((layer) => layer.source);

    // Send layers up to parent component
    onLayersUpdate(activeLayers);
  }, [projects, layerStates, onLayersUpdate]);

  // This component doesn't render anything visible
  return null;
};

export default ProjectLayersManager;
